/*
 * @Author: Yun 
 * @Date: 2025-11-24 14:43:19 
 * @Last Modified by: Yun
 * @Last Modified time: 2025-12-31 19:21:09
 */

import React, { useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { content } from '@configs/content';
import styles from './index.module.scss';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const RightPath: React.FC = () => {
  const { title, intro, items } = content.rightPath;
  const containerRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const introInnerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardsWrapperRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const hasScrolledRef = useRef(false);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [isFlipping, setIsFlipping] = useState(false);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let currentSpacing = 0;
    let isUpdating = false;

    // 更新卡片间距（使用 RAF 持续更新）
    const updateCardSpacing = () => {
      const targetSpacing = Math.min(Math.abs(velocityRef.current) * 1.5, 80);
      
      // 更快的插值系数，让动画更跟手
      currentSpacing += (targetSpacing - currentSpacing) * 0.5;
      
      // 批量更新所有卡片，使用 will-change 优化
      cardsRef.current.forEach((card, index) => {
        if (card && !card.classList.contains(styles.active)) {
          const offset = currentSpacing * index;
          card.style.transform = `translateX(${offset}px)`;
        }
      });
      
      // 如果还在拖拽，继续更新
      if (isDraggingRef.current) {
        requestAnimationFrame(updateCardSpacing);
      } else {
        isUpdating = false;
      }
    };

    // 惯性滚动动画
    const applyInertia = () => {
      if (Math.abs(velocityRef.current) < 0.5) {
        velocityRef.current = 0;
        currentSpacing = 0;
        // 重置所有卡片（排除激活的卡片，避免干扰 GSAP 动画）
        cardsRef.current.forEach((card) => {
          if (card && !card.classList.contains(styles.active)) {
            card.style.transform = '';
          }
        });
        animationFrameRef.current = null;
        return;
      }

      container.scrollLeft -= velocityRef.current;
      velocityRef.current *= 0.95;
      
      updateCardSpacing();
      
      animationFrameRef.current = requestAnimationFrame(applyInertia);
    };

    const handleMouseDown = (e: MouseEvent) => {
      // 如果有激活的卡片，禁用滚动
      if (activeCardId !== null) return;

      // 检查是否点击了按钮或其子元素
      const target = e.target as HTMLElement;
      if (target.closest('a[href]')) {
        return; // 不拦截按钮点击
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      isDraggingRef.current = true;
      hasScrolledRef.current = false;
      startXRef.current = e.pageX - container.offsetLeft;
      scrollLeftRef.current = container.scrollLeft;
      lastXRef.current = e.pageX;
      lastTimeRef.current = Date.now();
      velocityRef.current = 0;
      currentSpacing = 0;
      
      // 立即重置（排除激活的卡片）
      cardsRef.current.forEach((card) => {
        if (card && !card.classList.contains(styles.active)) {
          card.style.transform = '';
        }
      });
      
      // 启动持续更新
      if (!isUpdating) {
        isUpdating = true;
        requestAnimationFrame(updateCardSpacing);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || activeCardId !== null) return;
      
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startXRef.current) * 2;
      
      // 检测是否发生了滚动（超过 5px 阈值）
      if (Math.abs(walk) > 5) {
        hasScrolledRef.current = true;
      }
      
      container.scrollLeft = scrollLeftRef.current - walk;

      // 计算速度
      const now = Date.now();
      const dt = now - lastTimeRef.current;
      if (dt > 0) {
        const dx = e.pageX - lastXRef.current;
        const newVelocity = (dx / dt) * 16;
        velocityRef.current = velocityRef.current * 0.7 + newVelocity * 0.3;
      }
      lastXRef.current = e.pageX;
      lastTimeRef.current = now;
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        
        if (Math.abs(velocityRef.current) > 1) {
          animationFrameRef.current = requestAnimationFrame(applyInertia);
        } else {
          velocityRef.current = 0;
          currentSpacing = 0;
          cardsRef.current.forEach((card) => {
            if (card && !card.classList.contains(styles.active)) {
              card.style.transform = '';
            }
          });
        }
        
        // 延迟重置 hasScrolledRef，防止拖拽后立即触发点击
        setTimeout(() => {
          hasScrolledRef.current = false;
        }, 100);
      }
    };

    const handleMouseLeave = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        
        if (Math.abs(velocityRef.current) > 1) {
          animationFrameRef.current = requestAnimationFrame(applyInertia);
        } else {
          velocityRef.current = 0;
          currentSpacing = 0;
          cardsRef.current.forEach((card) => {
            if (card && !card.classList.contains(styles.active)) {
              card.style.transform = '';
            }
          });
        }
        
        // 延迟重置 hasScrolledRef，防止拖拽后立即触发点击
        setTimeout(() => {
          hasScrolledRef.current = false;
        }, 100);
      }
    };

    // Touch 事件处理
    const handleTouchStart = (e: TouchEvent) => {
      // 如果有激活的卡片，禁用滚动
      if (activeCardId !== null) return;

      const target = e.target as HTMLElement;
      if (target.closest('a[href]')) {
        return;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      const touch = e.touches[0];
      isDraggingRef.current = true;
      hasScrolledRef.current = false;
      startXRef.current = touch.pageX - container.offsetLeft;
      scrollLeftRef.current = container.scrollLeft;
      lastXRef.current = touch.pageX;
      lastTimeRef.current = Date.now();
      velocityRef.current = 0;
      currentSpacing = 0;

      cardsRef.current.forEach((card) => {
        if (card && !card.classList.contains(styles.active)) {
          card.style.transform = '';
        }
      });
      
      // 启动持续更新
      if (!isUpdating) {
        isUpdating = true;
        requestAnimationFrame(updateCardSpacing);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current || activeCardId !== null) return;

      const touch = e.touches[0];
      const x = touch.pageX - container.offsetLeft;
      const walk = (x - startXRef.current) * 2;
      
      // 检测是否发生了滚动（超过 5px 阈值）
      if (Math.abs(walk) > 5) {
        hasScrolledRef.current = true;
      }
      
      container.scrollLeft = scrollLeftRef.current - walk;

      const now = Date.now();
      const dt = now - lastTimeRef.current;
      if (dt > 0) {
        const dx = touch.pageX - lastXRef.current;
        const newVelocity = (dx / dt) * 16;
        velocityRef.current = velocityRef.current * 0.7 + newVelocity * 0.3;
      }
      lastXRef.current = touch.pageX;
      lastTimeRef.current = now;
    };

    const handleTouchEnd = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;

        if (Math.abs(velocityRef.current) > 1) {
          animationFrameRef.current = requestAnimationFrame(applyInertia);
        } else {
          velocityRef.current = 0;
          currentSpacing = 0;
          cardsRef.current.forEach((card) => {
            if (card && !card.classList.contains(styles.active)) {
              card.style.transform = '';
            }
          });
        }
        
        // 延迟重置 hasScrolledRef，防止拖拽后立即触发点击
        setTimeout(() => {
          hasScrolledRef.current = false;
        }, 100);
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      isUpdating = false;
    };
  }, [activeCardId]);

  useGSAP(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const introElement = introRef.current;
    const introInnerElement = introInnerRef.current;
    const scrollContainer = scrollContainerRef.current;

    // 检测是否为移动端
    const isMobile = window.innerWidth <= 768;

    // 设置初始状态
    if (introElement) {
      // 设置每个段落的初始状态
      const introParagraphs = introElement.querySelectorAll(`.${styles.introContent}`);
      
      introParagraphs.forEach((paragraph) => {
        gsap.set(paragraph, {
          opacity: 0,
          x: -30,
          y: 20,
        });
      });
    }

    if (scrollContainer) {
      gsap.set(scrollContainer, {
        x: '100vw',  // 初始位置在屏幕右侧外
      });
      // 初始禁用交互
      scrollContainer.style.pointerEvents = 'none';
    }
    
    if (introInnerElement) {
      gsap.set(introInnerElement, { y: 0 });
    }

    // 入场动画 - 标题和文案（只在首次进入或完全离开后重新进入时触发）
    const entranceTl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top 80%',
        end: 'bottom 20%',
        onEnter: () => entranceTl.restart(),
        // 移除 onEnterBack，避免逆向滚动时立即重置
      }
    });

    // 标题动画
    entranceTl.fromTo(
      `.${styles.title}`,
      { opacity: 0, y: -30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out'
      },
      0
    );

    // 分割线动画
    entranceTl.fromTo(
      `.${styles.divider}`,
      { scaleX: 0 },
      {
        scaleX: 1,
        duration: 0.8,
        ease: 'power3.out'
      },
      0.2
    );

    // 段落依次出现动画 - 从上到下
    if (introElement) {
      const introParagraphs = introElement.querySelectorAll(`.${styles.introContent}`);
      
      introParagraphs.forEach((paragraph, index) => {
        entranceTl.fromTo(
          paragraph,
          { opacity: 0, x: -30, y: 20 },
          {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 0.8,
            ease: 'power3.out'
          },
          0.4 + index * 0.15  // 每个段落延迟 0.15 秒
        );
      });
    }

    // 主时间轴 - 固定在视窗内，增加滚动距离以延长停留时间
    const mainTl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top top',
        end: isMobile ? '+=3700' : '+=5500',  // 增加总滚动距离
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      }
    });

    // 文案内部滚动动画（PC 和移动端都需要）
    if (introElement && introInnerElement) {
      // 获取容器的实际可用高度（减去 padding）
      const containerStyle = window.getComputedStyle(introElement);
      const paddingTop = parseFloat(containerStyle.paddingTop);
      const paddingBottom = parseFloat(containerStyle.paddingBottom);
      const containerHeight = introElement.clientHeight - paddingTop - paddingBottom;
      
      // 获取内容的实际高度
      const contentHeight = introInnerElement.scrollHeight;
      
      // 计算需要滚动的距离
      const scrollDistance = Math.max(0, contentHeight - containerHeight);
      
      if (scrollDistance > 0) {
        // 文案滚动动画 - 延长时长确保滚动完整
        mainTl.to(introInnerElement, {
          y: -scrollDistance,
          duration: 2,
          ease: 'power1.inOut'
        }, 0);
        
        // 增加停留时间，让用户看完内容
        mainTl.to({}, { duration: 1 }, 2);
      }
    }

    // 介绍文案淡出
    if (introElement) {
      mainTl.to(introElement, {
        opacity: 0,
        y: -30,
        duration: 0.5,
        ease: 'power2.in',
        onStart: () => {
          if (introElement) {
            introElement.style.pointerEvents = 'none';
          }
        },
        onReverseComplete: () => {
          if (introElement) {
            introElement.style.pointerEvents = 'auto';
          }
        }
      }, 3);
    }

    // 豆腐块从右往左移入（横移时间不变）
    if (scrollContainer) {
      mainTl.to(scrollContainer, {
        x: 0,  // 移动到原位
        duration: 0.6,  // 横移时间保持不变
        ease: 'power2.out',
        onStart: () => {
          if (scrollContainer) {
            scrollContainer.style.pointerEvents = 'auto';
          }
        },
        onComplete: () => {
          // 动画完成后清除 GSAP 的 transform，避免与拖拽冲突
          if (scrollContainer) {
            gsap.set(scrollContainer, { clearProps: 'transform' });
          }
        },
        onReverseComplete: () => {
          if (scrollContainer) {
            scrollContainer.style.pointerEvents = 'none';
          }
        }
      }, 3.8);
      
      // 增加豆腐块的停留时间（在横移完成后添加空白时间）
      mainTl.to({}, { duration: 3 }, 4.4);  // 增加1.5秒的停留时间
    }
  }, { scope: containerRef });

  // 提取逆向翻转逻辑为独立方法
  const resetCard = () => {
    if (isFlipping || activeCardId === null) return;

    setIsFlipping(true);

    const cardIndex = items.findIndex(item => item.id === activeCardId);
    const activeCard = cardsRef.current[cardIndex];
    
    if (!activeCard || !scrollContainerRef.current) {
      setIsFlipping(false);
      return;
    }

    const isMobile = window.innerWidth <= 768;

    const tl = gsap.timeline({
      onComplete: () => {
        setIsFlipping(false);
      }
    });

    if (isMobile) {
      // 移动端：翻转和尺寸变化同步
      tl.to(activeCard.querySelector(`.${styles.cardInner}`), {
        rotateY: 0,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0);

      // 先移除 active 类，让 CSS 恢复原始高度，然后再动画
      tl.call(() => {
        activeCard.classList.remove(styles.active);
      }, [], 0);

      tl.to(activeCard, {
        width: '',
        x: 0,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0);

      cardsRef.current.forEach((card, index) => {
        if (card && index !== cardIndex) {
          tl.to(card, {
            opacity: 1,
            duration: 0.6,
            ease: 'power2.in'
          }, 0);
        }
      });

      tl.call(() => {
        setActiveCardId(null);
      }, [], 0.6);
    } else {
      // 桌面端：保持原有逻辑
      tl.to(activeCard.querySelector(`.${styles.cardInner}`), {
        rotateY: 0,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0);

      tl.to(activeCard, {
        width: '',
        height: '',
        x: 0,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0.5);

      cardsRef.current.forEach((card, index) => {
        if (card && index !== cardIndex) {
          tl.to(card, {
            opacity: 1,
            duration: 0.25,
            ease: 'power2.in'
          }, 0.5);
        }
      });

      tl.call(() => {
        setActiveCardId(null);
      }, [], 1.1);
    }
  };

  // 监听组件离开视窗
  useGSAP(() => {
    if (!containerRef.current) return;

    const st = ScrollTrigger.create({
      trigger: containerRef.current,
      start: 'top bottom',
      end: 'bottom top',
      onLeave: resetCard,
      onLeaveBack: resetCard,
    });

    return () => {
      st.kill();
    };
  }, { scope: containerRef });

  const handleCardClick = (cardId: number, e: React.MouseEvent) => {
    // 如果正在翻转、正在拖拽或刚刚发生了滚动，则不触发点击
    if (isFlipping || isDraggingRef.current || hasScrolledRef.current) return;

    setIsFlipping(true);
    setActiveCardId(cardId);

    const cardIndex = items.findIndex(item => item.id === cardId);
    const activeCard = cardsRef.current[cardIndex];
    
    if (!activeCard || !scrollContainerRef.current) return;

    const cardRect = activeCard.getBoundingClientRect();
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    
    const isMobile = window.innerWidth <= 768;
    
    let targetWidth, targetHeight;
    if (isMobile) {
      // 移动端：75vw 宽度，高度写死 55vh
      targetWidth = window.innerWidth * 0.75;
      targetHeight = window.innerHeight * 0.55;
    } else {
      // 桌面端：保持原有逻辑
      targetWidth = containerRect.width * 0.6;
      targetHeight = targetWidth * (9 / 16);
    }
    
    const targetLeft = (containerRect.width - targetWidth) / 2;
    const currentLeft = cardRect.left - containerRect.left;
    const deltaX = targetLeft - currentLeft;

    const tl = gsap.timeline({
      onComplete: () => {
        setIsFlipping(false);
      }
    });

    // 移动端：翻转和尺寸变化同步进行
    if (isMobile) {
      // 同时进行翻转和尺寸变化
      tl.to(activeCard, {
        width: targetWidth,
        height: targetHeight,
        x: deltaX,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0);

      tl.to(activeCard.querySelector(`.${styles.cardInner}`), {
        rotateY: 180,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0);

      // 隐藏其他卡片（稍微延迟，避免影响当前卡片）
      cardsRef.current.forEach((card, index) => {
        if (card && index !== cardIndex) {
          tl.to(card, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.out'
          }, 0.2);
        }
      });
    } else {
      // 桌面端：保持原有逻辑
      tl.to(activeCard.querySelector(`.${styles.cardInner}`), {
        rotateY: 180,
        duration: 0.1,
        ease: 'power2.inOut'
      }, 0);

      cardsRef.current.forEach((card, index) => {
        if (card && index !== cardIndex) {
          tl.to(card, {
            opacity: 0,
            duration: 0.25,
            ease: 'power2.out'
          }, 0.5);
        }
      });

      tl.to(activeCard, {
        width: targetWidth,
        height: targetHeight,
        x: deltaX,
        duration: 0.6,
        ease: 'power2.inOut'
      }, 0.65);
    }
  };

  const handleDetailClick = (e: React.MouseEvent) => {
    // 检查是否点击了按钮
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest(`.${styles.button}`)) {
      return; // 不拦截按钮点击
    }

    // 复用逆向翻转逻辑
    resetCard();
  };

  return (
    <section 
      ref={containerRef} 
      className={styles.rightPathSection} 
      id="rightpath"
      onClick={(e) => {
        // 如果有active卡片，点击组件任意位置（除了按钮）都可以翻转回去
        if (activeCardId !== null) {
          const target = e.target as HTMLElement;
          if (target.closest('button') || target.closest(`.${styles.button}`)) return;
          handleDetailClick(e);
        }
      }}
    >
      <div className={styles.header}>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.divider} />
      </div>

      {/* 介绍文案模块 */}
      {intro && (
        <div ref={introRef} className={styles.intro}>
          <div ref={introInnerRef} className={styles.introInner}>
            {intro.content?.map((paragraph, index) => (
              <p key={index} className={styles.introContent} data-index={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      )}

      <div 
        ref={scrollContainerRef}
        className={`${styles.scrollContainer} ${activeCardId ? styles.expanded : ''}`}
      >
        <div 
          ref={cardsWrapperRef}
          className={styles.cardsWrapper}
        >
          {items.map((item, index) => (
            <div
              key={item.id}
              ref={(el) => {
                cardsRef.current[index] = el;
              }}
              className={`${styles.card} ${activeCardId === item.id ? styles.active : ''}`}
              style={{ 
                pointerEvents: isFlipping 
                  ? 'none' 
                  : (activeCardId !== null && activeCardId !== item.id) 
                    ? 'none'  // 有 active 卡片时，禁用其他卡片的点击
                    : 'auto'
              }}
              onClick={(e) => {
                // 只处理未激活卡片的点击
                if (activeCardId !== item.id) {
                  e.preventDefault();
                  e.stopPropagation(); // 阻止事件冒泡到section
                  handleCardClick(item.id, e);
                }
              }}
              onTouchStart={(e) => {
                // 阻止触摸事件冒泡
                if (activeCardId !== item.id) {
                  e.stopPropagation();
                }
              }}
            >
              <div className={styles.cardInner}>
                <div className={styles.cardFront}>
                  <div className={styles.imageWrapper}>
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className={styles.image}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    <div className={styles.cardOverlay}>
                      <p className={styles.cardTitle}>{item.title}</p>
                    </div>
                  </div>
                </div>

                <div className={styles.cardBack}>
                  <div className={styles.detailImageWrapper}>
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className={styles.detailImage}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    <div className={styles.detailOverlay} />
                  </div>
                  <div className={styles.detailContent}>
                    {/* <button
                      className={styles.backButton}
                      style={{ pointerEvents: 'auto' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        resetCard();
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <div className={styles.arrowCircle}>
                        <svg className={styles.arrow} viewBox="0 0 24 24" fill="none">
                          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>{content.rightPath.backButtonText}</span>
                    </button> */}
                    <p className={styles.detailDescription}>
                      {item.description || 'Explore the comprehensive vision and strategic approach towards building a better future for all.'}
                    </p>
                    <button
                      className={styles.button}
                      style={{ pointerEvents: 'auto' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(item.url, '_blank', 'noopener,noreferrer');
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <div className={styles.arrowCircle}>
                        <svg className={styles.arrow} viewBox="0 0 24 24" fill="none">
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>{content.rightPath.learnMoreButtonText}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RightPath;
